'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sponsor } from '@/lib/db/schema'

interface SponsorCarouselProps {
  sponsors: Sponsor[]
  autoPlay?: boolean
  interval?: number
}

export function SponsorCarousel({ sponsors, autoPlay = true, interval = 5000 }: SponsorCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)

  // Randomize sponsors on mount
  const [randomizedSponsors, setRandomizedSponsors] = useState<Sponsor[]>([])

  useEffect(() => {
    if (sponsors.length > 0) {
      const shuffled = [...sponsors].sort(() => Math.random() - 0.5)
      setRandomizedSponsors(shuffled)
    }
  }, [sponsors])

  useEffect(() => {
    if (!autoPlay || isHovered || randomizedSponsors.length <= 1) return

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % randomizedSponsors.length)
    }, interval)

    return () => clearInterval(timer)
  }, [autoPlay, interval, isHovered, randomizedSponsors.length])

  const handleSponsorClick = async (sponsor: Sponsor) => {
    try {
      // Track click
      await fetch(`/api/sponsors/${sponsor.id}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referrer: window.location.href }),
      })
    } catch (error) {
      console.error('Error tracking sponsor click:', error)
    }

    // Open sponsor URL
    window.open(sponsor.url, '_blank', 'noopener,noreferrer')
  }

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % randomizedSponsors.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + randomizedSponsors.length) % randomizedSponsors.length)
  }

  if (randomizedSponsors.length === 0) {
    return null
  }

  return (
    <section className="py-12 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Our Sponsors
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Thank you to our sponsors who help keep Undernet running
          </p>
        </div>

        <div 
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-lg">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="flex items-center justify-center p-12 min-h-[200px]"
              >
                <button
                  onClick={() => handleSponsorClick(randomizedSponsors[currentIndex])}
                  className="group transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
                >
                  <div className="flex flex-col items-center space-y-4">
                    {randomizedSponsors[currentIndex].logoUrl ? (
                      <img
                        src={randomizedSponsors[currentIndex].logoUrl}
                        alt={`${randomizedSponsors[currentIndex].name} logo`}
                        className="max-w-[300px] max-h-[120px] object-contain transition-all duration-300 group-hover:brightness-110"
                      />
                    ) : (
                      <div className="text-3xl font-bold text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors">
                        {randomizedSponsors[currentIndex].name}
                      </div>
                    )}
                    <div className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-primary transition-colors">
                      Click to visit website
                    </div>
                  </div>
                </button>
              </motion.div>
            </AnimatePresence>
          </div>

          {randomizedSponsors.length > 1 && (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-800"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {randomizedSponsors.length > 1 && (
            <div className="flex justify-center space-x-2 mt-6">
              {randomizedSponsors.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-primary scale-125'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

